'use client';

import { useState } from 'react';
import { ArrowRight, Heart } from '@phosphor-icons/react';
import { useTranslations } from 'next-intl';

interface WaitlistFormProps {
  className?: string;
  /** Layout: 'inline' for side-by-side input+button, 'stacked' for vertical */
  layout?: 'inline' | 'stacked';
  /** Social proof text, e.g. "1,247 dog owners on the waitlist" */
  socialProof?: string;
  /** List tag for segmenting signups (e.g. 'community_waitlist') */
  listTag?: string;
}

export default function WaitlistForm({
  className = '',
  layout = 'inline',
  socialProof,
  listTag,
}: WaitlistFormProps) {
  const t = useTranslations('landing.waitlist');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || status === 'loading') return;

    setStatus('loading');
    setErrorMsg('');

    try {
      const res = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), ...(listTag && { listTag }) }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setStatus('success');
      } else {
        setStatus('error');
        setErrorMsg(data.error || t('errorGeneric'));
      }
    } catch {
      setStatus('error');
      setErrorMsg(t('errorConnection'));
    }
  }

  if (status === 'success') {
    return (
      <div className={`bg-teal-50 border border-teal-200 rounded-card p-6 animate-fade-in ${className}`}>
        <div className="flex items-center justify-center gap-2 mb-2">
          <Heart weight="fill" className="text-coral-400 w-5 h-5" />
          <p className="font-heading font-semibold text-dark">{t('successTitle')}</p>
        </div>
        <p className="text-sm text-gray-600 text-center">
          {t('successMessage')}
        </p>
      </div>
    );
  }

  return (
    <div className={className}>
      <form
        onSubmit={handleSubmit}
        className={layout === 'inline' ? 'flex gap-3' : 'space-y-3'}
      >
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('placeholder')}
          required
          disabled={status === 'loading'}
          className="flex-1 px-4 py-3.5 bg-surface border border-border rounded-brand text-base text-dark placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:opacity-50 min-h-[48px]"
          aria-label={t('emailLabel')}
        />
        <button
          type="submit"
          disabled={status === 'loading' || !email.trim()}
          className={`flex-shrink-0 px-6 py-3.5 bg-coral-400 hover:bg-coral-500 active:bg-coral-600 text-white font-heading font-semibold text-sm rounded-brand transition-all disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px] flex items-center justify-center gap-2 hover:shadow-glow-coral ${
            layout === 'stacked' ? 'w-full' : ''
          }`}
        >
          {status === 'loading' ? (
            <span className="inline-flex gap-1">
              <span className="w-1.5 h-1.5 bg-surface rounded-full animate-bounce" />
              <span className="w-1.5 h-1.5 bg-surface rounded-full animate-bounce delay-100" />
              <span className="w-1.5 h-1.5 bg-surface rounded-full animate-bounce delay-200" />
            </span>
          ) : (
            <>
              {t('joinButton')}
              <ArrowRight weight="bold" className="w-4 h-4" />
            </>
          )}
        </button>
      </form>
      {status === 'error' && (
        <p className="text-sm text-error mt-2">{errorMsg}</p>
      )}
      {socialProof && status !== 'error' && (
        <p className="text-xs text-gray-400 mt-3 text-center">{socialProof}</p>
      )}
    </div>
  );
}
